/**
 * EJEMPLOS SECTION — Rediseño B2B SaaS Profesional
 * 
 * Estilo coherente con secciones anteriores:
 * - Fondo: #f7f9fb
 * - Colores: #0b1220, #6b7280, emerald/red solo en iconos
 * - Mockup minimalista tipo dashboard real
 * - Sin gradientes decorativos
 * - Animaciones reveal con Intersection Observer
 */

import React, { useState, useRef, useEffect } from 'react';
import { 
  AlertTriangle, 
  TrendingDown,
  Users,
  Truck,
  DollarSign,
  Bell,
  CheckCircle2,
  XCircle,
  Calendar,
  MapPin,
  Fuel,
  ArrowRight
} from 'lucide-react';

// ─── Intersection Observer Hook ──────────────────────────────────────────────
const useReveal = (delay = 0) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(16px)';
    el.style.transition = `opacity 0.5s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.5s cubic-bezier(0.16,1,0.3,1) ${delay}ms`;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
          observer.disconnect();
        }
      },
      { threshold: 0.08 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);
  return ref;
};

// ─── Main Component ───────────────────────────────────────────────────────────
const Ejemplos = () => {
  const [alertaActiva, setAlertaActiva] = useState(0);

  const headerRef = useReveal(0);
  const tiposRef = useReveal(0);
  const tabsRef = useReveal(0);
  const mockupRef = useReveal(100);

  const ejemplosAlertas = [
    {
      tipo: "Viaje no rentable",
      icono: <TrendingDown />,
      iconColor: "text-red-600",
      iconBg: "bg-red-50",
      titulo: "Viaje con pérdida detectada",
      descripcion: "El sistema detectó que este viaje cerró en negativo",
      detalles: {
        viaje: "Lima - Arequipa",
        fecha: "15 Feb 2026",
        vehiculo: "VXI-789",
        metricas: [
          { label: "Ingreso", value: "S/ 3,500", type: "neutral" },
          { label: "Costos", value: "S/ 4,350", type: "neutral" },
          { label: "Resultado", value: "-S/ 850", type: "negative" }
        ],
        factores: [
          "Combustible: S/ 1,800 (15% sobre promedio)",
          "Peajes: S/ 450",
          "Chofer: S/ 800",
          "Mantenimiento: S/ 300"
        ],
        recomendacion: "Este viaje no es rentable con la tarifa actual. Considera negociar S/ 4,800 como mínimo o rechazar viajes similares."
      }
    },
    {
      tipo: "Cliente problemático",
      icono: <Users />,
      iconColor: "text-amber-600",
      iconBg: "bg-amber-50",
      titulo: "Cliente con rentabilidad baja",
      descripcion: "Este cliente genera volumen pero poca utilidad",
      detalles: {
        cliente: "Distribuidora Central SAC",
        periodo: "Últimos 2 meses",
        viajes: "12 viajes",
        metricas: [
          { label: "Ingreso total", value: "S/ 38,400", type: "neutral" },
          { label: "Costo total", value: "S/ 36,200", type: "neutral" },
          { label: "Margen", value: "5.7%", type: "negative" }
        ],
        factores: [
          "Margen muy bajo (objetivo: 15-20%)",
          "Rutas con retorno vacío frecuente",
          "Pagos con 45-60 días de demora",
          "Extras no facturados: S/ 800"
        ],
        recomendacion: "Renegociar tarifas o reducir frecuencia. Este cliente consume capacidad que podrías usar para clientes más rentables."
      }
    },
    {
      tipo: "Vehículo costoso",
      icono: <Truck />,
      iconColor: "text-amber-600",
      iconBg: "bg-amber-50",
      titulo: "Vehículo con costos elevados",
      descripcion: "Este camión está gastando más de lo normal",
      detalles: {
        vehiculo: "ABC-123",
        periodo: "Últimos 30 días",
        consumo: "8.5 km/gal vs 11 km/gal",
        metricas: [
          { label: "Promedio", value: "S/ 1,400/viaje", type: "neutral" },
          { label: "Actual", value: "S/ 1,820/viaje", type: "neutral" },
          { label: "Exceso", value: "+30%", type: "negative" }
        ],
        factores: [
          "Consumo elevado: 8.5 km/gal (vs 11 km/gal)",
          "Mantenimiento: S/ 2,400 este mes",
          "3 viajes con rutas ineficientes",
          "Posible problema mecánico"
        ],
        recomendacion: "Revisar estado mecánico urgente. El exceso de consumo está eliminando el margen de ganancia."
      }
    },
    {
      tipo: "Flujo de caja",
      icono: <DollarSign />,
      iconColor: "text-red-600",
      iconBg: "bg-red-50",
      titulo: "Flujo de caja negativo proyectado",
      descripcion: "En 15 días podrías tener problemas de liquidez",
      detalles: {
        proyeccion: "Al 1 Marzo 2026",
        pendiente: "S/ 18,500",
        egresos: "S/ 24,300",
        metricas: [
          { label: "Ingresos", value: "S/ 18,500", type: "neutral" },
          { label: "Egresos", value: "S/ 24,300", type: "neutral" },
          { label: "Diferencia", value: "-S/ 5,800", type: "negative" }
        ],
        factores: [
          "Planilla choferes: S/ 12,000 (vence 28 Feb)",
          "Cuotas vehículos: S/ 6,500 (vence 5 Mar)",
          "Combustible: S/ 3,800 (semanal)",
          "Mantenimientos: S/ 2,000"
        ],
        recomendacion: "Cobrar facturas vencidas de Distribuidora Central (S/ 8,400) o gestionar anticipo de próximos viajes."
      }
    }
  ];

  const tiposAlertas = [
    {
      nombre: "Alertas críticas",
      icono: <AlertTriangle />,
      descripcion: "Requieren atención inmediata",
      ejemplos: ["Viajes con pérdida", "Flujo de caja negativo", "Costos fuera de control"]
    },
    {
      nombre: "Alertas de optimización",
      icono: <TrendingDown />,
      descripcion: "Oportunidades de mejora",
      ejemplos: ["Clientes con margen bajo", "Rutas ineficientes", "Vehículos poco rentables"]
    },
    {
      nombre: "Alertas informativas",
      icono: <Bell />,
      descripcion: "Información relevante",
      ejemplos: ["Pagos por vencer", "Mantenimientos programados", "Metas cumplidas"]
    }
  ];

  const alerta = ejemplosAlertas[alertaActiva];

  return (
    <section
      id="ejemplos"
      className="w-full py-24 lg:py-28"
      style={{ backgroundColor: '#f7f9fb' }}
    >
      <div className="max-w-[1120px] mx-auto px-6 flex flex-col gap-12">

        {/* ── Header ── */}
        <header ref={headerRef} className="max-w-[720px] flex flex-col gap-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-50 border border-amber-200 w-fit">
            <Bell size={14} strokeWidth={2} className="text-amber-600" />
            <span className="text-[13px] font-medium text-amber-700">Sistema en acción</span>
          </div>

          <h2 className="text-[28px] font-bold text-[#0b1220] leading-[1.3] tracking-[-0.01em]">
            Así te avisa el sistema cuando{' '}
            <span className="text-red-600">detecta algo importante</span>
          </h2>

          <p className="text-[15px] text-[#6b7280] leading-[1.6] max-w-[640px]">
            <strong className="text-[#0b1220] font-semibold">Alertas automáticas en tiempo real</strong>{' '}
            para que nunca pierdas dinero sin darte cuenta. El sistema analiza cada viaje y te notifica cuando encuentra problemas o oportunidades.
          </p>
        </header>

        {/* ── Tipos de alertas ── */}
        <div
          ref={tiposRef}
          className="grid grid-cols-1 sm:grid-cols-3 gap-4"
        >
          {tiposAlertas.map((tipo, i) => (
            <div
              key={i}
              className="flex flex-col gap-3 p-5 bg-white rounded-lg border border-black/[0.08] shadow-[0_4px_12px_rgba(15,23,42,0.03)] hover:shadow-[0_8px_20px_rgba(15,23,42,0.06)] hover:border-[#0b63c6]/40 transition-all duration-300"
            >
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                {React.cloneElement(tipo.icono, {
                  size: 20,
                  strokeWidth: 1.75,
                  className: 'text-[#0b63c6]'
                })}
              </div>
              <div>
                <h4 className="text-[14px] font-semibold text-[#0b1220] mb-1">
                  {tipo.nombre}
                </h4>
                <p className="text-[12.5px] text-[#6b7280] leading-[1.6] mb-2">
                  {tipo.descripcion}
                </p>
                <ul className="flex flex-col gap-1">
                  {tipo.ejemplos.map((ej, idx) => (
                    <li key={idx} className="flex items-center gap-1.5">
                      <CheckCircle2 size={12} strokeWidth={2} className="text-emerald-500 shrink-0" />
                      <span className="text-[12px] text-[#6b7280]">{ej}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* ── Tabs selector ── */}
        <div ref={tabsRef} className="flex flex-col gap-4">
          <h3 className="text-[18px] font-bold text-[#0b1220] text-center">
            Ejemplos reales de alertas
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {ejemplosAlertas.map((item, i) => (
              <button
                key={i}
                onClick={() => setAlertaActiva(i)}
                className={`
                  flex items-center gap-3 p-4 rounded-lg border text-left transition-all duration-200
                  ${alertaActiva === i
                    ? 'bg-white border-[#0b63c6] shadow-[0_8px_20px_rgba(11,99,198,0.12)]'
                    : 'bg-white border-black/[0.08] hover:border-black/[0.12] hover:shadow-[0_4px_12px_rgba(15,23,42,0.06)]'
                  }
                `}
              >
                <div className={`w-11 h-11 rounded-lg ${item.iconBg} flex items-center justify-center shrink-0`}>
                  {React.cloneElement(item.icono, {
                    size: 20,
                    strokeWidth: 2,
                    className: item.iconColor
                  })}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-semibold text-[#6b7280] uppercase tracking-wide mb-0.5">
                    {item.tipo}
                  </div>
                  <div className="text-[14px] font-semibold text-[#0b1220] truncate">
                    {item.titulo}
                  </div>
                </div>
                {alertaActiva === i && (
                  <CheckCircle2 size={18} strokeWidth={2} className="text-[#0b63c6] shrink-0" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ── Mockup de alerta ── */}
        <div
          ref={mockupRef}
          className="bg-white rounded-xl border border-black/[0.08] shadow-[0_10px_25px_rgba(15,23,42,0.05)] overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-start gap-4 p-6 bg-gradient-to-r from-gray-50 to-white border-b border-black/[0.06]">
            <div className={`w-12 h-12 rounded-lg ${alerta.iconBg} flex items-center justify-center shrink-0`}>
              {React.cloneElement(alerta.icono, {
                size: 24,
                strokeWidth: 2,
                className: alerta.iconColor
              })}
            </div>
            <div className="flex-1">
              <div className="text-[11px] font-semibold text-[#6b7280] uppercase tracking-wide mb-1">
                {alerta.tipo}
              </div>
              <h4 className="text-[17px] font-bold text-[#0b1220] leading-tight mb-1">
                {alerta.titulo}
              </h4>
              <p className="text-[13.5px] text-[#6b7280]">
                {alerta.descripcion}
              </p>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 shrink-0">
              <AlertTriangle size={14} strokeWidth={2} className="text-amber-600" />
              <span className="text-[11px] font-semibold text-amber-700">
                Requiere atención
              </span>
            </div>
          </div>

          {/* Body */}
          <div className="p-6 space-y-5">
            {/* Métricas */}
            <div className="grid grid-cols-3 gap-3">
              {alerta.detalles.metricas.map((metrica, i) => (
                <div
                  key={i}
                  className={`
                    p-3 rounded-lg border text-center
                    ${metrica.type === 'negative'
                      ? 'bg-red-50 border-red-200'
                      : 'bg-gray-50 border-black/[0.06]'
                    }
                  `}
                >
                  <div className="text-[11px] font-medium text-[#6b7280] uppercase tracking-wide mb-1">
                    {metrica.label}
                  </div>
                  <div
                    className={`text-[18px] font-bold ${
                      metrica.type === 'negative' ? 'text-red-600' : 'text-[#0b1220]'
                    }`}
                  >
                    {metrica.value}
                  </div>
                </div>
              ))}
            </div>

            {/* Factores */}
            <div className="space-y-2">
              <h5 className="text-[13px] font-semibold text-[#0b1220] uppercase tracking-wide">
                Factores identificados
              </h5>
              <div className="space-y-1.5 p-3 rounded-lg bg-gray-50">
                {alerta.detalles.factores.map((factor, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <XCircle size={14} strokeWidth={2} className="text-red-500 shrink-0 mt-0.5" />
                    <span className="text-[13px] text-[#6b7280]">{factor}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Recomendación */}
            <div className="flex items-start gap-3 p-4 rounded-lg bg-emerald-50 border border-emerald-200">
              <CheckCircle2 size={18} strokeWidth={2} className="text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <div className="text-[13px] font-semibold text-emerald-900 mb-1">
                  Recomendación del sistema
                </div>
                <p className="text-[13px] text-emerald-800 leading-[1.6]">
                  {alerta.detalles.recomendacion}
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-4 bg-gray-50 border-t border-black/[0.06]">
            <button className="text-[13px] font-medium text-[#6b7280] hover:text-[#0b1220] transition-colors">
              Marcar como revisado
            </button>
            <button className="flex items-center gap-1.5 bg-[#0b63c6] hover:bg-[#0957b0] text-white text-[13px] font-semibold px-4 py-2 rounded-lg transition-colors duration-200">
              Ver detalles
              <ArrowRight size={14} strokeWidth={2} />
            </button>
          </div>
        </div>

        {/* ── Beneficios (compacto) ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            {
              num: "1",
              titulo: "Detectas problemas antes de que sean graves",
              desc: "El sistema te avisa al día siguiente si un viaje cerró en pérdida."
            },
            {
              num: "2",
              titulo: "Tomas acción inmediata",
              desc: "Con información clara y recomendaciones, sabes exactamente qué hacer."
            },
            {
              num: "3",
              titulo: "Evitas pérdidas recurrentes",
              desc: "Una vez identificado, dejas de perder dinero mes tras mes."
            },
            {
              num: "4",
              titulo: "Optimizas tu operación",
              desc: "Las alertas te muestran patrones: qué vehículos gastan más, qué rutas funcionan mejor."
            }
          ].map((item, i) => (
            <div
              key={i}
              className="flex gap-4 p-5 bg-white rounded-lg border border-black/[0.08] hover:border-[#0b63c6]/40 hover:shadow-[0_8px_20px_rgba(15,23,42,0.06)] transition-all duration-300"
            >
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                <span className="text-[18px] font-bold text-[#0b63c6]">{item.num}</span>
              </div>
              <div>
                <h4 className="text-[14px] font-semibold text-[#0b1220] leading-tight mb-1">
                  {item.titulo}
                </h4>
                <p className="text-[13px] text-[#6b7280] leading-[1.6]">
                  {item.desc}
                </p>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default Ejemplos;